<script setup>

import DateRangePicker from "@/components/DateRangePicker.vue";
import ButtonDropdownFilter from "@/components/ButtonDropdownFilter.vue";

import {useHasAbility} from "@/composables/hasAbility";
import {useLocales} from "@/composables/locales";
import {useRoute, useRouter} from "vue-router";
import {useSwal} from "@/composables/useSwal";
import {computed, nextTick, onMounted, ref, watch} from "vue";
import Ls from "@/services/ls";
import {BASE_URL} from "@/services/api";
import {useStore} from "vuex";
import PageLoader from "@/components/PageLoader.vue";

const { can } = useHasAbility()
const {locale, datatableLocaleUrl} = useLocales()
const router = useRouter()
const route = useRoute()
const store = useStore()
const {showSwal} = useSwal()

const table = ref('')
const query = ref({
  project_id: "",
  startDate: "",
  endDate: "",
  orderBy: 7
})

const projects = computed(() => store.getters['projects/getProjectsCollection'])
const criterias = computed(() => store.getters['projects/getUserPerformCriterias'])


watch(() => query.value, (value) => {
  nextTick(() => fetchDatas())
}, { deep: true })
watch(() => locale.value, (value) => {
  nextTick(() => fetchDatas())
}, { deep: true })

function handleRangeDate (range) {
  query.value.startDate = range.start_date
  query.value.endDate = range.end_date
}
function fetchDatas() {
  const AUTH_TOKEN = Ls.get('auth.token')
  const pageLimit = Ls.get('pageLength')

  const params = new URLSearchParams({...query.value});

  const element = document.getElementById("performance_customer_datatable");

  if(element){
    table.value = $("#performance_customer_datatable").DataTable({
      destroy: true,
      responsive: !0,
      searchDelay: 500,
      processing: !0,
      serverSide: !0,
      pageLength: pageLimit ||  10,
      lengthMenu: [5, 10, 25, 50, 75, 100 ],
      order: [[ query.value?.orderBy || 7, 'desc']],
      ajax: {
        url: BASE_URL + "/get/users/performances/stats/datatables?" + params.toString(),
        type: "GET",

        beforeSend: function (request, options) {
          request.setRequestHeader("Authorization", "Bearer " + AUTH_TOKEN);
          request.setRequestHeader("Company", Ls.get('current.company'));
        }
      },
      columns: [
        { data: "rank" },{ data: "name" }, { data: "project_count" },{ data: "alert_count" },{ data: "critical_alert_count" },{ data: "late_task_count" }, { data: "completed_task_count" },{ data: "percentage_completed" }
      ],
      columnDefs: [

        {
          targets: -8,
          orderable: false,
          className: "dt-center text-center clickable" ,
          render: function (t, a, e, l) {
            return '<span class="badge badge-primary" data-id="'+ e.id +'">' + t + "</span>";
          },
        },
        {
          targets: -7,
          className: "dt-center text-center clickable" ,
          render: function (t, a, e, l) {
            return '<span data-id="'+ e.id +'">' + t + "</span>";
          },
        },
        {
          targets: -6,
          className: "dt-center text-center clickable",
          render: function (t, a, e, l) {
            return '<span data-id="'+ e.id +'">' + t + "</span>";
          },
        },
        {
          targets: -5,
          order: "desc",
          className: "dt-center text-center clickable",
          render: function (t, a, e, l) {
            return '<span data-id="'+ e.id +'">' + t + "</span>";
          },
        },
        {
          targets: -4,
          className: "dt-center text-center clickable",
          render: function (t, a, e, l) {

            return '<span data-id="'+ e.id +'">' + t + "</span>";
          },
        },
        {
          targets: -3,
          className: "dt-center text-center clickable" ,
          render: function (t, a, e, l) {
            return '<span data-id="'+ e.id +'">' + t + "/"+ e.task_count + "</span>";
          },
        },

        {
          targets: -2,
          className: "dt-center text-center clickable" ,
          render: function (t, a, e, l) {
            return '<span data-id="'+ e.id +'">' + t + "/"+ e.task_count + "</span>";
          },
        },

        {
          targets: -1,
          className: "dt-center text-center",
          render: (t, a, e, l) => {
            return '<span data-id="'+ e.id +'">' + t + "</span>";
          },
        },

      ],
      language: {
        url: datatableLocaleUrl.value,
      }
    });

    table.value?.on('order', function() {
      const order = table.value.order();
      query.value.orderBy = order[0][0];
    });
  }


}


onMounted( () => {
  store.dispatch("projects/loadProjectsCollection")
  store.dispatch("projects/loadUserPerformCriteria")
})


</script>

<template>
  <PageLoader v-if="!table"/>
  <div class="card">
    <div class="card-header flex-wrap pt-6 pb-0">
      <div class="card-title">
        <div class="card-label">
          <h3 class="card-label mb-2">{{ $t('performance.list') }}</h3>
          <p class="fs-6" v-html="orderByName"></p>
        </div>
      </div>
      <div class="card-toolbar">
        <ButtonDropdownFilter
            :btnTitle="$t('performance.order.orderBy')"
            :search="false"
            :options="criterias"
            value-field="order_column"
            :name-field="locale"
            v-model="query.orderBy"
        />
        <ButtonDropdownFilter
            :btnTitle="$t('performance.projects')"
            :search="false"
            :options="projects"
            value-field="id"
            name-field="title"
            v-model="query.project_id"
        />
        <DateRangePicker id="estimate_date_id" @update:date="handleRangeDate" />
      </div>
    </div>
    <div class="card-body">
      <table class="table align-middle table-row-bordered fs-6 gy-3 gx-4" id="performance_customer_datatable">
        <thead class="mb-5">
        <tr class="fw-bold fs-6" style="background-color: #F2F3F4;">
          <th class="w-50px text-center">{{ $t("performance.table.position") }} </th>
          <th class="text-center">{{ $t('performance.table.collaborator') }} </th>
          <th class="text-center">{{ $t('performance.table.folders')}}</th>
          <th class="text-center">{{ $t("performance.table.alerts") }}</th>
          <th class="text-center">{{ $t("performance.table.alarms") }}</th>
          <th class="text-center">{{ $t("performance.table.lateTask") }}</th>
          <th class="text-center">{{ $t("performance.table.doneTasks") }}</th>
          <th class="text-center">{{ $t('performance.table.donePercent') }}</th>
        </tr>
        </thead>
      </table>
    </div>
  </div>
</template>

<style scoped>

</style>